import { cssVars } from '@/app/styles';
import { useMemo } from 'react';
import styled from 'styled-components';

const StatusBox = styled.div`
  min-width: 54px;
  padding-left: 6px;
  padding-right: 6px;
  height: 22px;
  font-size: 12px;
  box-sizing: border-box;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
  line-height: 21px; /* once less than height due to unknown padding/whitespace to top of div */
  color: ${(props) => props?.fg};
  background-color: ${(props) => props?.bg};
  border: solid 1px ${(props) => props?.border};
  text-align: center;
  text-transform: capitalize;
`;

function cycleStatusCellColors() {
  let ticker = 0;
  const colorMemory = {};

  function inner(status) {
    const color = cssVars.color.statusColors?.[status] ?? colorMemory[status];
    if (color) return color;

    const currentCycledColor = cssVars.color.extraStatusColors[ticker];

    colorMemory[status] = currentCycledColor;
    ticker = ++ticker % cssVars.color.extraStatusColors.length;

    return currentCycledColor;
  }

  return inner;
}

const getStatusCellColors = cycleStatusCellColors();

const StatusBoxContainer = styled.div`
  display: flex;
  gap: 5px;
`;

/**
 * This component renders the status box. The box colors depend on the value prop.
 *
 * @param {string} props.value Status value as defined by Deckard. Used for determining color
 * @param {string} props.valueFormatted Status value the user will see. It is mapped to a Deckard
 * @param {({data}) => any} props.secondaryStatusValueGetter Function that takes in row data
 *   license status
 * and returns the value of the secondary status to display
 * @param {({value}) => string} props.secondaryStatusValueFormatter Function that takes in the
 * value of the secondary status and returns a user-friendly label to display
 */
export function StatusCell({
  value,
  valueFormatted,
  getStatusDisplayName: providedGetStatusDisplayName,
  ariaLabel = '',
}) {
  const statusesWithColors = useMemo(() => {
    const statuses = Array.isArray(value) ? value : [value];

    const getStatusDisplayName = !providedGetStatusDisplayName
      ? () => valueFormatted
      : providedGetStatusDisplayName;

    return statuses.filter(Boolean).map((status) => ({
      displayName: getStatusDisplayName(status) || status,
      status,
      colorProps: getStatusCellColors(status?.toLowerCase()),
    }));
  }, [providedGetStatusDisplayName, value, valueFormatted]);

  if (!statusesWithColors.length) return <div />;

  return (
    <StatusBoxContainer aria-label={ariaLabel}>
      {statusesWithColors.map(({ status, displayName, colorProps }) => (
        <StatusBox key={status} {...colorProps}>
          {displayName}
        </StatusBox>
      ))}
    </StatusBoxContainer>
  );
}
